/**
 * Encrypted Files Page
 *
 * Security Design:
 * - Shows file METADATA only (name, size, date, algorithm)
 * - Never displays file content or encryption keys
 * - Decrypt button triggers OTP verification flow
 * - Delete button removes file with confirmation
 */

import React, { useState, useEffect } from 'react';
import { listEncryptedFiles, deleteEncryptedFile, cancelOwnScheduledDelete, requestOTP, verifyOTP } from '../services/api';
import { formatDateTime as tzFormatDateTime } from '../utils/time';

const OTP_ACTION_FILE_DELETE = 'file_delete';

function EncryptedFilesPage({ onDecryptRequest }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteOtpPhase, setDeleteOtpPhase] = useState(null); // null | 'request' | 'verify'
  const [deleteOtpSending, setDeleteOtpSending] = useState(false);
  const [deleteOtpCode, setDeleteOtpCode] = useState('');
  const [deleteOtpError, setDeleteOtpError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [search, setSearch] = useState(''); // client-side search filter

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      const data = await listEncryptedFiles();
      setFiles(data);
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const fileIdFromConfirm = deleteConfirm && (typeof deleteConfirm === 'string' ? deleteConfirm : deleteConfirm.id);

  const handleDelete = async (fileOrId) => {
    const id = typeof fileOrId === 'string' ? fileOrId : (fileOrId?.id);
    if (!id) return;
    setDeleting(true);
    setDeleteOtpError('');
    try {
      await deleteEncryptedFile(id);
      setFiles(prev => prev.filter(f => f.id !== id));
      setDeleteConfirm(null);
      setDeleteOtpPhase(null);
      setDeleteOtpCode('');
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      const isOtpRequired =
        msg.includes('otp') && (msg.includes('verification') || msg.includes('verify otp') || msg.includes('verify first'));
      if (isOtpRequired) {
        setDeleteOtpPhase('request');
      } else {
        alert('Failed to delete: ' + (err?.message || 'Unknown error'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const sendDeleteOtp = async () => {
    if (!fileIdFromConfirm) return;
    setDeleteOtpSending(true);
    setDeleteOtpError('');
    try {
      await requestOTP(fileIdFromConfirm, OTP_ACTION_FILE_DELETE);
      setDeleteOtpPhase('verify');
      setDeleteOtpCode('');
    } catch (err) {
      setDeleteOtpError(err?.message || 'Failed to send OTP');
    } finally {
      setDeleteOtpSending(false);
    }
  };

  const verifyAndDelete = async () => {
    if (!fileIdFromConfirm || !deleteOtpCode.trim()) {
      setDeleteOtpError('Please enter the 6-digit code');
      return;
    }
    setDeleting(true);
    setDeleteOtpError('');
    try {
      await verifyOTP(fileIdFromConfirm, deleteOtpCode.trim(), OTP_ACTION_FILE_DELETE);
      await deleteEncryptedFile(fileIdFromConfirm);
      setFiles(prev => prev.filter(f => f.id !== fileIdFromConfirm));
      setDeleteConfirm(null);
      setDeleteOtpPhase(null);
      setDeleteOtpCode('');
    } catch (err) {
      setDeleteOtpError(err?.message || 'Verification failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelScheduled = async (fileId) => {
    setCancellingId(fileId);
    try {
      await cancelOwnScheduledDelete(fileId);
      await loadFiles();
    } catch (err) {
      alert('Failed to cancel scheduled deletion: ' + (err?.message || 'Unknown error'));
    } finally {
      setCancellingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Use shared timezone-aware utility
  const formatDate = (dateStr) => tzFormatDateTime(dateStr);

  // File type icon based on extension
  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const types = {
      pdf: { label: 'PDF', color: '#EF4444', bg: '#fef2f2' },
      doc: { label: 'DOC', color: '#6366F1', bg: 'var(--blue-muted)' },
      docx: { label: 'DOC', color: '#6366F1', bg: 'var(--blue-muted)' },
      xls: { label: 'XLS', color: '#10B981', bg: '#f0fdf4' },
      xlsx: { label: 'XLS', color: '#10B981', bg: '#f0fdf4' },
      ppt: { label: 'PPT', color: '#ea580c', bg: '#fff7ed' },
      pptx: { label: 'PPT', color: '#ea580c', bg: '#fff7ed' },
      jpg: { label: 'IMG', color: '#8B5CF6', bg: '#f5f3ff' },
      jpeg: { label: 'IMG', color: '#8B5CF6', bg: '#f5f3ff' },
      png: { label: 'IMG', color: '#8B5CF6', bg: '#f5f3ff' },
      gif: { label: 'IMG', color: '#8B5CF6', bg: '#f5f3ff' },
      zip: { label: 'ZIP', color: '#ca8a04', bg: '#fefce8' },
      rar: { label: 'ZIP', color: '#ca8a04', bg: '#fefce8' },
      txt: { label: 'TXT', color: 'var(--text-muted)', bg: '#f8fafc' },
      csv: { label: 'CSV', color: '#10B981', bg: '#f0fdf4' },
      mp4: { label: 'VID', color: '#db2777', bg: '#fdf2f8' },
      mp3: { label: 'AUD', color: '#0891b2', bg: '#ecfeff' },
    };
    return types[ext] || { label: 'FILE', color: 'var(--text-muted)', bg: '#f8fafc' };
  };

  return (
    <div className="page-container admin-page-shell admin-tactile-page">
      <div style={styles.pageHeader} className="ef-page-header">
        <div style={{ flex: 1 }}>
          <h2 style={styles.pageTitle}>My Encrypted Files</h2>
          <p style={styles.subtitle}>
            {loading ? 'Loading your files...' : `${files.length} encrypted file${files.length !== 1 ? 's' : ''} stored securely.`}
            {' '}Only metadata is shown -- file content is encrypted and inaccessible to the server.
          </p>
        </div>
        {!loading && files.length > 0 && (
          <input
            type="text"
            placeholder="Search files..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.searchInput}
            className="ef-search-input"
          />
        )}
      </div>

      {/* Decrypt flow explanation — addresses user confusion about encrypted downloads
         The OTP page handles: download encrypted blob → decrypt locally → auto-save plaintext.
         Users never manually handle encrypted files. */}
      <div style={styles.decryptNotice}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <span>
          <strong>How decryption works:</strong> When you click "Decrypt", you'll verify via OTP, then enter your password.
          Decryption is performed <strong>locally on your device</strong> — the server never sees plaintext.
          The decrypted file downloads automatically to your device.
          <strong> Access expiry</strong> (for shared files) only affects downloading from the server again — it does not delete a plaintext file you already saved locally.
        </span>
      </div>

      {loading && (
        <div style={styles.fileList}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ ...styles.fileCard, opacity: 1 - i * 0.15 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px' }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--border)', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite', animationDelay: `${i * 120}ms` }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: 14, width: '45%', borderRadius: 6, background: 'var(--border)', opacity: 0.4, marginBottom: 8 }} />
                  <div style={{ height: 10, width: '30%', borderRadius: 4, background: 'var(--border)', opacity: 0.3 }} />
                </div>
                <div style={{ width: 80, height: 34, borderRadius: 10, background: 'var(--border)', opacity: 0.25 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && (() => {
        const filtered = files.filter(f =>
          f.original_filename.toLowerCase().includes(search.toLowerCase())
        );
        if (files.length === 0) return (
          <div style={styles.emptyState}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <p style={{ margin: '12px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
              No encrypted files yet. Upload your first file to get started.
            </p>
          </div>
        );
        if (filtered.length === 0) return (
          <div style={styles.emptyState}>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              No files matching "{search}".
            </p>
          </div>
        );
        return (
        <div style={styles.fileList}>
          {filtered.map((file) => {
            const icon = getFileIcon(file.original_filename);
            return (
              <div key={file.id} style={styles.fileCard} className="ef-file-card">
                <div style={styles.fileMain} className="ef-file-main">
                  {/* File type icon */}
                  <div style={{ ...styles.fileIcon, backgroundColor: icon.bg, color: icon.color }} className="ef-file-icon">
                    {icon.label}
                  </div>

                  {/* File info */}
                  <div style={styles.fileInfo} className="ef-file-info">
                    <div style={styles.fileName} className="ef-file-name">{file.original_filename}</div>
                    <div style={styles.fileMeta} className="ef-file-meta">
                      <span>{formatFileSize(file.file_size)}</span>
                      <span style={styles.metaDot} />
                      <span>{formatDate(file.uploaded_at)}</span>
                      {file.scheduled_delete_at && (
                        <>
                          <span style={styles.metaDot} />
                          <span style={{ color: 'var(--red)', fontWeight: 600 }}>Scheduled for deletion</span>
                        </>
                      )}
                      <span style={styles.metaDot} />
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                        {file.file_hash.substring(0, 12)}...
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={styles.fileActions} className="ef-file-actions">
                    {file.security_level && (
                      <span style={{
                        ...styles.levelBadge,
                        backgroundColor: file.security_level === 'maximum' ? '#f5f3ff' :
                                         file.security_level === 'standard' ? 'var(--blue-light)' : 'var(--green-light)',
                        color: file.security_level === 'maximum' ? '#8B5CF6' :
                               file.security_level === 'standard' ? 'var(--blue)' : 'var(--green)',
                      }} className="ef-level-badge">
                        {file.security_level.toUpperCase()}
                      </span>
                    )}
                    <span className="security-badge green ef-algo-badge">
                      {file.encryption_algorithm}
                    </span>
                    <button style={styles.decryptBtn} className="ef-decrypt-btn" onClick={() => onDecryptRequest(file.id)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Decrypt
                    </button>
                    {file.scheduled_delete_at && (
                      <button
                        style={{ ...styles.decryptBtn, backgroundColor: 'var(--text-secondary)' }}
                        onClick={() => handleCancelScheduled(file.id)}
                        disabled={cancellingId === file.id}
                        title="Cancel scheduled deletion"
                        className="ef-cancel-btn"
                      >
                        {cancellingId === file.id ? 'Cancelling...' : 'Cancel scheduled'}
                      </button>
                    )}
                    <button
                      style={styles.deleteBtn}
                      className="ef-delete-btn"
                      onClick={() => setDeleteConfirm(file.id)}
                      title="Delete file"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6"/>
                        <path d="M14 11v6"/>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Security summary row */}
                <div style={styles.securityRow} className="ef-security-row">
                  <div style={styles.securityItem}>
                    <span style={styles.securityLabel}>Encrypted locally</span>
                    <span className="security-badge green">Yes</span>
                  </div>
                  <div style={styles.securityItem}>
                    <span style={styles.securityLabel}>Integrity</span>
                    <span className="security-badge green">Verified</span>
                  </div>
                  <div style={styles.securityItem}>
                    <span style={styles.securityLabel}>Decryption</span>
                    <span className="security-badge blue">OTP Required</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        );
      })()}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={styles.modalOverlay} onClick={() => { setDeleteConfirm(null); setDeleteOtpPhase(null); setDeleteOtpCode(''); setDeleteOtpError(''); }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--text-primary)' }}>
              {deleteOtpPhase ? 'OTP required to delete' : 'Delete Encrypted File?'}
            </h3>
            {!deleteOtpPhase ? (
              <>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 20px', lineHeight: 1.5 }}>
                  This will permanently delete the encrypted file from the server.
                  This action cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button style={styles.cancelBtn} onClick={() => { setDeleteConfirm(null); setDeleteOtpPhase(null); }}>
                    Cancel
                  </button>
                  <button
                    style={styles.confirmDeleteBtn}
                    onClick={() => handleDelete(deleteConfirm)}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete File'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
                  {deleteOtpPhase === 'request'
                    ? 'This action requires OTP verification. We\'ll send a code to your email.'
                    : 'Enter the 6-digit code sent to your email.'}
                </p>
                {deleteOtpPhase === 'verify' && (
                  <div style={{ marginBottom: 12 }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={deleteOtpCode}
                      onChange={(e) => setDeleteOtpCode(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={e => { if (e.key === 'Enter' && deleteOtpCode.length === 6) verifyAndDelete(); }}
                      style={{ padding: '8px 12px', width: '100%', boxSizing: 'border-box', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 14 }}
                    />
                  </div>
                )}
                {deleteOtpError && (
                  <p style={{ fontSize: 12, color: 'var(--red)', margin: '0 0 12px' }}>{deleteOtpError}</p>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button
                    style={styles.cancelBtn}
                    onClick={() => { setDeleteConfirm(null); setDeleteOtpPhase(null); setDeleteOtpCode(''); setDeleteOtpError(''); }}
                  >
                    Cancel
                  </button>
                  {deleteOtpPhase === 'request' ? (
                    <button style={styles.confirmDeleteBtn} onClick={sendDeleteOtp} disabled={deleteOtpSending}>
                      {deleteOtpSending ? 'Sending...' : 'Send OTP'}
                    </button>
                  ) : (
                    <button style={styles.confirmDeleteBtn} onClick={verifyAndDelete} disabled={deleting}>
                      {deleting ? 'Deleting...' : 'Verify & Delete'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  pageHeader: {
    marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: 18, flexWrap: 'wrap',
  },
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px 0', letterSpacing: '-0.4px', lineHeight: 1.1,
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6, fontWeight: 500 },
  searchInput: {
    padding: '10px 16px', borderRadius: 14, border: '1.5px solid rgba(99,102,241,0.12)',
    fontSize: 13, width: 220, backgroundColor: 'var(--surface-90)', color: 'var(--text-body)',
    transition: 'all 0.3s ease', outline: 'none',
  },

  decryptNotice: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '14px 18px',
    background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(6,182,212,0.04))',
    border: '1.5px solid rgba(16,185,129,0.15)', borderRadius: 16,
    marginBottom: 18, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5,
    backdropFilter: 'blur(8px)',
  },

  emptyState: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))',
    padding: 56, borderRadius: 24,
    textAlign: 'center', border: '1.5px solid rgba(99,102,241,0.08)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    backdropFilter: 'blur(12px)',
  },

  fileList: { display: 'flex', flexDirection: 'column', gap: 14 },

  fileCard: {
    backgroundColor: 'var(--surface-85)', borderRadius: 20,
    border: '1.5px solid rgba(99,102,241,0.08)', overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(99,102,241,0.06), 0 1px 3px rgba(0,0,0,0.02)',
    transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
    backdropFilter: 'blur(12px)',
  },
  fileMain: {
    display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px',
  },
  fileIcon: {
    width: 48, height: 48, borderRadius: 16, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px',
    flexShrink: 0, boxShadow: '0 3px 10px rgba(99,102,241,0.1)',
  },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: {
    fontSize: 14, fontWeight: 700, color: 'var(--text-primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  fileMeta: {
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)',
    marginTop: 5, flexWrap: 'wrap', fontWeight: 500,
  },
  metaDot: {
    width: 3, height: 3, borderRadius: '50%', backgroundColor: 'var(--text-secondary)', flexShrink: 0,
    opacity: 0.5,
  },
  fileActions: {
    display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
  },
  levelBadge: {
    padding: '3px 10px', borderRadius: 8, fontSize: 10, fontWeight: 800,
    letterSpacing: '0.5px', whiteSpace: 'nowrap',
  },
  decryptBtn: {
    padding: '8px 18px',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
    color: '#fff', border: 'none',
    borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 700,
    boxShadow: '0 4px 14px rgba(99,102,241,0.25)',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
  deleteBtn: {
    padding: '8px 10px',
    background: 'linear-gradient(135deg, rgba(239,68,68,0.06), rgba(239,68,68,0.03))',
    color: 'var(--text-muted)',
    border: '1.5px solid rgba(239,68,68,0.15)', borderRadius: 12, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },

  securityRow: {
    display: 'flex', gap: 16, padding: '12px 24px',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.03), rgba(139,92,246,0.02))',
    borderTop: '1px solid rgba(99,102,241,0.06)',
    flexWrap: 'wrap',
  },
  securityItem: {
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500,
  },
  securityLabel: { color: 'var(--text-secondary)' },

  // Delete confirmation modal
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,16,40,0.5)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 1000,
    backdropFilter: 'blur(6px)',
  },
  modal: {
    backgroundColor: 'var(--surface-95)', borderRadius: 24,
    padding: '28px', width: '100%', maxWidth: 440,
    border: '1.5px solid rgba(99,102,241,0.1)',
    boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 8px 24px rgba(0,0,0,0.08)',
    backdropFilter: 'blur(20px)',
  },
  cancelBtn: {
    padding: '10px 20px', backgroundColor: 'var(--surface-90)', color: 'var(--text-secondary)',
    border: '1.5px solid rgba(99,102,241,0.12)', borderRadius: 14, cursor: 'pointer',
    fontSize: 14, fontWeight: 700,
    transition: 'all 0.25s ease',
  },
  confirmDeleteBtn: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #EF4444, #DC2626)',
    color: '#fff',
    border: 'none', borderRadius: 14, cursor: 'pointer',
    fontSize: 14, fontWeight: 700,
    boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
    transition: 'all 0.25s ease',
  },
};

export default EncryptedFilesPage;
